import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams, useNavigationType } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { UserContext } from '../context/UserContext';
import { useProjectStore, useProjectStoreBase, useArchivedProjectStore, useArchivedProjectStoreBase } from '../stores/pmProjectStore';
import { ConfirmModal, Table, TableHeader, TableBody, TableHead, TableRow, TableCell, CustomSelect, Button, BulkSelectCell, TableSkeleton, Amount, RowActions, RowAction, ListPagination, ListSearchInput, ListEmptyRow, BulkSelectHeader, MultiBulkActionButton, btnPrimary, btnDisabled } from '@redpanda/ui';
import { useBulkActions, createBulkDeleteOptimisticHandler, createBulkDeleteSuccessHandler, createOptimisticBulkDeleteUpdate, isAdmin, useUrlPage, useSyncUrlPage, useUrlSearch } from '@redpanda/utils';
import ProjectDialog from '../components/ProjectDialog';
import ProjectLimitBanner from '../components/ProjectLimitBanner';
import { useProjectLimitCache } from '../hooks/useProjectLimitCache';
import { useListSearch } from '../hooks/useListSearch';
import { getProjectStatusOptions } from '../utils/constants';
import { canDeleteProject } from '../utils/permissions';
import { notifyDataChanged } from '../utils/dataEvents';
import { pendingRowClass } from '../utils/pendingItem';
import { bulkDeleteProjects, setProjectsArchived, cloneProject, getProjects, getArchivedProjects, updateProject } from '../services/api';
import { PMProject } from '../types/pm';
import ProjectProgressBar from '../components/ProjectProgressBar';

interface ProjectListPageProps {
  isArchived?: boolean;
}

const ProjectListPage: React.FC<ProjectListPageProps> = ({ isArchived = false }) => {
  const { t } = useTranslation();
  const { user } = useContext(UserContext)!;
  const userRole = user?.role;

  const activeStore = useProjectStore();
  const archivedStore = useArchivedProjectStore();
  const currentStore = isArchived ? archivedStore : activeStore;
  const storeBase = isArchived ? useArchivedProjectStoreBase : useProjectStoreBase;

  const {
    projects,
    pagination,
    error,
    isLoading,
    isPageLoading,
    showDeleteConfirmModal,
    isDeleting,
    setCurrentPage,
    handleDelete,
    confirmDelete,
    closeDeleteModal,
    fetchProjects,
  } = currentStore;

  const { canCreateProject, setCanCreateProject, limitBannerRefreshTrigger, setLimitBannerRefreshTrigger } = useProjectLimitCache();

  const [dialogProject, setDialogProject] = useState<PMProject | null | undefined>(undefined); // undefined＝關閉、null＝新增
  const [updatingProjectId, setUpdatingProjectId] = useState<string | null>(null);

  // CRM 報價列表「建立 PM 專案」連過來（?fromQuote=報價 id）：直接開新增對話框並帶入報價，參數用過就清掉
  const [searchParams, setSearchParams] = useSearchParams();
  const [initialQuoteId, setInitialQuoteId] = useState<string>();
  useEffect(() => {
    const quoteId = searchParams.get('fromQuote');
    if (!quoteId || isArchived) return;
    setInitialQuoteId(quoteId);
    setDialogProject(null);
    setSearchParams(params => { params.delete('fromQuote'); return params; }, { replace: true });
  }, [searchParams, setSearchParams, isArchived]);

  // 網址 ?page=、?q=：從專案詳情返回時回到同一頁、保留搜尋
  const { pathname } = useLocation();
  const urlPage = { pathname, navigationType: useNavigationType(), searchParams, setSearchParams };
  const initialPage = useUrlPage(urlPage);
  const [searchTerm, setSearchTerm] = useUrlSearch(urlPage); // 切換「專案／已封存」時改讀新網址的 ?q=

  // 有快取先顯示、背景比對（createEntityStore）
  useEffect(() => {
    fetchProjects(initialPage);
  }, [fetchProjects, isArchived]); // eslint-disable-line react-hooks/exhaustive-deps

  // 搜尋由後端查全部專案（R29）；沒有搜尋時用列表 store
  const search = searchTerm.trim();
  const fetchList = isArchived ? getArchivedProjects : getProjects;
  const searchResult = useListSearch<PMProject>('projects', search ? { search, isArchived } : null, page => fetchList(page, { search }));
  const listItems = searchResult.active ? searchResult.items : projects;
  const listPagination = searchResult.active ? searchResult.pagination : pagination;
  const listLoading = searchResult.active ? searchResult.isLoading : isPageLoading;

  const canDelete = (project: PMProject) => canDeleteProject(project, userRole, user?.id);

  const bulkDeleteSuccess = createBulkDeleteSuccessHandler(() => storeBase.getState(), fetchProjects, setCurrentPage);
  const bulkActions = useBulkActions({
    items: listItems,
    idField: 'id',
    entityName: t('pmProjects.item', { ns: 'pages' }),
    bulkDeleteApi: bulkDeleteProjects,
    canDeleteItem: canDelete,
    userRole,
    t,
    toast,
    pagination: pagination ? { currentPage: pagination.currentPage, totalPages: pagination.totalPages } : undefined,
    fetchItems: fetchProjects,
    setCurrentPage,
    onOptimisticUpdate: createBulkDeleteOptimisticHandler(
      storeBase.setState,
      isArchived ? 'archivedProject' : 'project',
      () => projects,
      () => pagination
    ),
    onSuccess: async (deletedIds?: string[]) => {
      await bulkDeleteSuccess(deletedIds ?? []);
      setLimitBannerRefreshTrigger(prev => prev + 1);
      notifyDataChanged('projects');
    },
  });

  const changePage = (page: number) => (searchResult.active ? searchResult.setPage(page) : bulkActions.handlePageChange(page));
  useSyncUrlPage(urlPage, listPagination, changePage);

  // 封存／恢復：從目前列表移到另一個列表（照 CRM 客戶封存）
  const moveProjects = async (archived: boolean) => {
    const ids = bulkActions.selectedItems;
    if (ids.length === 0) {
      toast.error(t(archived ? 'pmProjects.list.selectProjectsForArchive' : 'pmProjects.list.selectProjectsForRestore', { ns: 'pages' }));
      return;
    }
    const sourceBase = archived ? useProjectStoreBase : useArchivedProjectStoreBase;
    const targetBase = archived ? useArchivedProjectStoreBase : useProjectStoreBase;
    const moved = listItems.filter(p => ids.includes(p.id)).map(p => ({ ...p, archived }));

    createOptimisticBulkDeleteUpdate(sourceBase.setState, archived ? 'project' : 'archivedProject')(ids, projects, pagination);
    bulkActions.setSelectedItems([]);

    try {
      const { count } = (await setProjectsArchived(ids, archived)).data;
      if (count > 0) {
        toast.success(t(archived ? 'pmProjects.list.archiveSuccess' : 'pmProjects.list.restoreSuccess', { count, ns: 'pages' }));
        const target = targetBase.getState();
        const existing = new Set(target.items.map(item => item.id));
        const added = moved.filter(p => !existing.has(p.id));
        if (added.length > 0) {
          targetBase.setState({
            items: [...added, ...target.items],
            pagination: target.pagination ? { ...target.pagination, totalItems: target.pagination.totalItems + added.length } : null,
          });
        }
        setLimitBannerRefreshTrigger(prev => prev + 1);
        notifyDataChanged('projects');
      }
      await sourceBase.getState().refreshData();
    } catch (err: any) {
      console.error(archived ? '封存專案失敗:' : '恢復專案失敗:', err);
      toast.error(err.response?.data?.error || t('common.messages.operationFailed'));
      await sourceBase.getState().refreshData();
    }
  };

  const handleConfirmDelete = async () => {
    await confirmDelete();
    setLimitBannerRefreshTrigger(prev => prev + 1);
  };

  const handleCloneProject = async (projectId: string) => {
    try {
      await cloneProject(projectId);
      toast.success(t('pmProjects.cloneSuccess', { ns: 'pages' }));
      await activeStore.refreshData();
      setLimitBannerRefreshTrigger(prev => prev + 1);
      notifyDataChanged('projects');
    } catch (err: any) {
      toast.error(err.response?.status === 403 ? t('pmProjects.limitReached', { ns: 'pages' }) : err.response?.data?.error || t('pmProjects.cloneError', { ns: 'pages' }));
    }
  };

  // 狀態下拉（樂觀更新，失敗回滾）
  const handleStatusChange = async (project: PMProject, newStatus: string) => {
    const previousStatus = project.status;
    storeBase.getState().updateItem(project.id, { status: newStatus });
    setUpdatingProjectId(project.id);
    try {
      await updateProject(project.id, { status: newStatus });
      toast.success(t('common.messages.updateSuccess'));
      notifyDataChanged('projects');
    } catch (err: any) {
      console.error('Failed to update project status:', err);
      storeBase.getState().updateItem(project.id, { status: previousStatus });
      toast.error(err.response?.data?.error || t('common.messages.updateFailed'));
    } finally {
      setUpdatingProjectId(null);
    }
  };

  const title = isArchived ? t('pmProjects.archived.title', { ns: 'pages' }) : t('pmProjects.title', { ns: 'pages' });

  const addButton = (
    <button
      type="button"
      onClick={() => canCreateProject && setDialogProject(null)}
      disabled={!canCreateProject}
      className={canCreateProject ? btnPrimary : btnDisabled}
      title={canCreateProject ? undefined : t('pmProjects.limitReached', { ns: 'pages' })}
    >
      {t('pmProjects.addTitle', { ns: 'pages' })}
    </button>
  );

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-4">
        <div className="flex items-baseline justify-between">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{title}</h1>
          <div className="flex space-x-3">
            {isAdmin(userRole) && (
              <MultiBulkActionButton
                selectedCount={bulkActions.selectedItems.length}
                isDeleting={bulkActions.isBulkDeleting}
                onDelete={bulkActions.handleBulkDelete}
                onArchive={isArchived ? undefined : () => moveProjects(true)}
                onRestore={isArchived ? () => moveProjects(false) : undefined}
              />
            )}
            {!isArchived && addButton}
            <Link to={isArchived ? '/projects' : '/projects/archived'}>
              <Button variant="secondary">
                {isArchived ? t('pmProjects.backToList', { ns: 'pages' }) : t('common.bulk.viewArchivedAction')}
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {!isArchived && (
        <ProjectLimitBanner onCanCreateProjectChange={setCanCreateProject} refreshTrigger={limitBannerRefreshTrigger} />
      )}

      {error && (
        <div className="mb-4 p-4 border border-red-300 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg">
          {error}
        </div>
      )}

      <div className="mb-4">
        <ListSearchInput value={searchTerm} onChange={setSearchTerm} placeholder={t('pmProjects.searchPlaceholder', { ns: 'pages' })} />
      </div>

      <ListPagination pagination={listPagination} onPageChange={changePage} isLoading={listLoading} className="mb-3" />

      {(isLoading && projects.length === 0 && !searchResult.active) || listLoading ? (
        <TableSkeleton rows={10} columns={9} showHeader={true} />
      ) : (
        <div className="bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg">
          <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <Table style={{ tableLayout: 'fixed', minWidth: '1120px' }}>
              <TableHeader>
                <TableRow>
                  <BulkSelectHeader
                    userRole={userRole}
                    allSelected={bulkActions.allSelected}
                    onToggleAll={bulkActions.toggleSelectAll}
                    hasItems={listItems.length > 0}
                  />
                  <TableHead>{t('pmProjects.fields.name', { ns: 'pages' })}</TableHead>
                  <TableHead className="w-28">{t('pmProjects.fields.projectType', { ns: 'pages' })}</TableHead>
                  <TableHead className="w-36">{t('pmProjects.fields.status', { ns: 'pages' })}</TableHead>
                  <TableHead className="w-36">{t('pmProjects.progress.label', { ns: 'pages' })}</TableHead>
                  <TableHead numeric className="w-32">{t('pmProjects.fields.totalBudget', { ns: 'pages' })}</TableHead>
                  <TableHead numeric className="w-32">{t('pmProjects.fields.paidAmount', { ns: 'pages' })}</TableHead>
                  <TableHead numeric className="w-32">{t('pmProjects.fields.pendingAmount', { ns: 'pages' })}</TableHead>
                  <TableHead className="w-36">{t('common.table.columns.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listItems.length === 0 ? (
                  <ListEmptyRow
                    colSpan={9}
                    filtered={!!search}
                    title={isArchived ? t('pmProjects.archived.empty', { ns: 'pages' }) : t('pmProjects.list.empty', { ns: 'pages' })}
                    hint={isArchived ? t('pmProjects.archived.emptyHint', { ns: 'pages' }) : t('pmProjects.list.emptyHint', { ns: 'pages' })}
                    action={!isArchived && canCreateProject && addButton}
                  />
                ) : (
                  listItems.map(project => (
                    <TableRow key={project.id} className={pendingRowClass(project)}>
                      <BulkSelectCell
                        userRole={userRole}
                        isSelected={bulkActions.selectedItems.includes(project.id)}
                        onToggle={() => bulkActions.toggleItemSelection(project.id)}
                        canDelete={canDelete(project)}
                      />
                      <TableCell className="truncate">
                        {/* D9：點名稱進詳情 */}
                        <Link to={`/projects/${project.id}`} className="font-medium text-fg hover:text-accent hover:underline">
                          {project.name}
                        </Link>
                      </TableCell>
                      <TableCell className="truncate">
                        {project.projectType ? t(`pmProjects.projectTypes.${project.projectType}`, { ns: 'pages' }) : <span className="text-fg-muted">—</span>}
                      </TableCell>
                      <TableCell>
                        {/* 暫停中滑過顯示原因（客戶無回應等） */}
                        <div className="w-28" title={project.status === 'ON_HOLD' && project.holdReason ? t(`pmProjects.holdReason.${project.holdReason}`, { ns: 'pages' }) : undefined}>
                          <CustomSelect
                            value={project.status}
                            onChange={value => handleStatusChange(project, value)}
                            disabled={updatingProjectId === project.id}
                            options={getProjectStatusOptions(t)}
                            className="text-sm"
                          />
                        </div>
                      </TableCell>
                      <TableCell><ProjectProgressBar progress={project.progress} /></TableCell>
                      <TableCell numeric>{project.totalBudget == null ? <span className="text-fg-muted">—</span> : <Amount value={project.totalBudget} />}</TableCell>
                      <TableCell numeric><Amount value={project.paidAmount ?? 0} /></TableCell>
                      <TableCell numeric>
                        {project.badDebtAt ? (
                          // 已列呆帳：待收款為 0，改顯示呆帳金額（紅字）
                          <span className="text-red-600 dark:text-red-400" title={project.badDebtNote || undefined}>
                            {t('pmProjects.badDebt.label', { ns: 'pages' })} <Amount value={project.badDebtAmount ?? 0} />
                          </span>
                        ) : <Amount value={project.pendingAmount ?? 0} />}
                      </TableCell>
                      <TableCell>
                        <RowActions>
                          <RowAction onClick={() => setDialogProject(project)}>{t('common.actions.edit')}</RowAction>
                          {!isArchived && (
                            <RowAction onClick={() => handleCloneProject(project.id)}>{t('pmProjects.clone', { ns: 'pages' })}</RowAction>
                          )}
                          {canDelete(project) && (
                            <RowAction tone="danger" onClick={() => handleDelete(project.id)}>{t('common.actions.delete')}</RowAction>
                          )}
                        </RowActions>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <ListPagination pagination={listPagination} onPageChange={changePage} isLoading={listLoading} className="mt-6" />

      <ConfirmModal
        isOpen={showDeleteConfirmModal}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        title={t('pmProjects.deleteConfirmTitle', { ns: 'pages' })}
        message={t('pmProjects.deleteConfirmMessage', { ns: 'pages' })}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={isDeleting}
        loadingText={t('common.status.deleting')}
        t={t}
      />

      <ConfirmModal
        isOpen={bulkActions.showBulkDeleteModal}
        onClose={bulkActions.closeBulkDeleteModal}
        onConfirm={bulkActions.confirmBulkDelete}
        title={t('common.bulk.confirmDeleteTitle', { count: bulkActions.selectedItems.length, entity: t('pmProjects.item', { ns: 'pages' }) })}
        message={t('common.bulk.confirmDeleteMessage', { count: bulkActions.selectedItems.length, entity: t('pmProjects.item', { ns: 'pages' }) })}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={bulkActions.isBulkDeleting}
        loadingText={t('common.status.deleting')}
        t={t}
      />

      {dialogProject !== undefined && (
        <ProjectDialog
          isOpen
          onClose={() => { setDialogProject(undefined); setInitialQuoteId(undefined); }}
          project={dialogProject ?? undefined}
          initialQuoteId={initialQuoteId}
        />
      )}
    </div>
  );
};

export default ProjectListPage;
